import React, { useEffect, useReducer, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CompetenceOrderGeneralDataCard from './CompetenceOrderGeneralDataCard';
import { initialState, reducer } from 'features/competenceOrder/reducers/generalData/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { competenceOrderValidator } from 'features/competenceOrder/validators';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { APPLICATION_STATUS_QUERY, DOCUMENT_TYPES_QUERY } from '../../../queries';
import { COMPETENCE_ORDERS_SAVE } from '../../../mutations';
import { COMPETENCE_ORDER_SAVE_SUBSCRIPTION } from '../../../subscriptions';

const CompetenceOrderGeneralDataContainer = ({ competenceOrderId, data, approvalTypes, onRedirect }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const [documentTypes, setDocumentTypes] = useState([]);
  const documentTypeId = -5;

  const [localState, dispatch] = useReducer(reducer, initialState);

  const [competenceOrderValidation, validateCompetenceOrder] = useValidation(competenceOrderValidator);

  // #region Queries

  const { data: applicationStatuses } = useQuery(APPLICATION_STATUS_QUERY);

  useQuery(DOCUMENT_TYPES_QUERY, {
    onCompleted: data => {
      setDocumentTypes(data.documentTypes);
    }
  });

  const documentTypeCode = useMemo(() => documentTypes.find(x => x.id === documentTypeId)?.code, [documentTypes, documentTypeId]);
  // #endregion

  // #region Mutations

  const [competenceOrderSave] = useMutation(COMPETENCE_ORDERS_SAVE);

  // #endregion

  // #region Subscriptions

  useSubscription(COMPETENCE_ORDER_SAVE_SUBSCRIPTION, {
    onData: ({ data: { data }, client }) => {
      addToast(t('CompetenceOrder.CompetenceOrderUpdated'), 'success');
      if (!competenceOrderId) onRedirect(data?.competenceOrderUpdated?.competenceOrderId);
      else
        client.cache.evict({
          id: `CompetenceOrder:{"competenceOrderId":${competenceOrderId ?? data?.competenceOrderUpdated?.competenceOrderId}}`
        });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'competenceOrders' });
      client.cache.gc();
    }
  });

  // #endregion

  // #region Handlers
  const handlePropertyChanged = useCallback(
    (propertyName, value) => {
      dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
    },
    [dispatch]
  );

  const handleSave = () => {
    if (!validateCompetenceOrder(localState.competenceOrder)) return;
    // eslint-disable-next-line no-unused-vars
    const { changed, statusName, approvalTypeName, ...competenceOrder } = localState.competenceOrder;

    dispatch({ type: 'OnHeaderSaved' });
    return competenceOrderSave({ variables: { input: { ...competenceOrder } } });
  };

  // #endregion

  // #region Side effects
  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
    if (!competenceOrderId) dispatch({ type: 'OnHeaderPropertyChanged', propertyName: 'documentTypeCode', value: documentTypeCode });
  }, [data, documentTypeCode, competenceOrderId, dispatch]);

  // #endregion

  return (
    <CompetenceOrderGeneralDataCard
      competenceOrder={localState.competenceOrder}
      applicationStatuses={applicationStatuses?.applicationStatus || []}
      approvalTypes={approvalTypes}
      validation={competenceOrderValidation}
      onSave={handleSave}
      onPropertyChanged={handlePropertyChanged}
    />
  );
};

CompetenceOrderGeneralDataContainer.propTypes = {
  competenceOrderId: PropTypes.number,
  data: PropTypes.object,
  approvalTypes: PropTypes.array,
  onRedirect: PropTypes.func
};

export default CompetenceOrderGeneralDataContainer;
